import type { Dispatch, SetStateAction } from 'react'
import type {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
} from '@mui/x-data-grid-premium'

import LeadTableDateFilter, { type LeadTableDateFilterValue } from './LeadTableDateFilter'
import type { DateFilterField } from '../leadTableQuery'
import type { LeadRow } from '../table/leadTableTypes'
import { renderHeaderWithFilter } from '../table/leadTableCellHelpers'
import { getFilterValue, replaceFilterItem } from './leadTableFilterModel'

type BuildLeadTableColumnsWithFiltersParams = {
  columns: GridColDef<LeadRow>[]
  timezone: string
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
  filterModel: GridFilterModel
  onFilterModelChange: Dispatch<SetStateAction<GridFilterModel>>
}

function buildDateFilterHeader(params: {
  field: DateFilterField
  title: string
  filterModel: GridFilterModel
  timezone: string
  onFilterModelChange: Dispatch<SetStateAction<GridFilterModel>>
  onPaginationModelChange: Dispatch<SetStateAction<GridPaginationModel>>
}) {
  const {
    field,
    title,
    filterModel,
    timezone,
    onFilterModelChange,
    onPaginationModelChange,
  } = params

  return renderHeaderWithFilter({
    filterNode: (
      <LeadTableDateFilter
        title={title}
        timezone={timezone}
        value={getFilterValue<LeadTableDateFilterValue>(
          filterModel,
          field,
        ) as LeadTableDateFilterValue | undefined}
        onChange={(value) => {
          onFilterModelChange((prev) =>
            replaceFilterItem(
              prev,
              field,
              value
                ? {
                    id: field,
                    field,
                    operator: value.operator,
                    value,
                  }
                : null,
            ),
          )

          onPaginationModelChange((prev) => ({ ...prev, page: 0 }))
        }}
      />
    ),
  })
}

export function buildLeadTableColumnsWithFilters({
  columns,
  timezone,
  onPaginationModelChange,
  filterModel,
  onFilterModelChange,
}: BuildLeadTableColumnsWithFiltersParams): GridColDef<LeadRow>[] {
  return columns.map((column) => {
    switch (column.field) {
      case 'createdAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'createdAt',
              title: 'Date',
              filterModel,
              timezone,
              onFilterModelChange,
              onPaginationModelChange,
            }),
        }

      case 'statusChangedAt':
        return {
          ...column,
          renderHeader: () =>
            buildDateFilterHeader({
              field: 'statusChangedAt',
              title: 'Status Changed',
              filterModel,
              timezone,
              onFilterModelChange,
              onPaginationModelChange,
            }),
        }

      default:
        return column
    }
  })
}
